<template>
  <a-spin :spinning="loading">
    <div class="potential-donors-page">
      <a-breadcrumb style="margin: 16px 0px;">
        <a-breadcrumb-item>产品开发者</a-breadcrumb-item>
        <a-breadcrumb-item>概览</a-breadcrumb-item>
        <a-breadcrumb-item>潜在捐赠伙伴</a-breadcrumb-item>
      </a-breadcrumb>

      <section class="partners-panel">
        <header class="panel-header">
          <div class="title-wrap">
            <h2 class="panel-title">潜在捐赠伙伴</h2>
            <span class="panel-count">{{ partners.length }} 家</span>
          </div>
          <p class="panel-note">注：近一年有已通过兼容性测评的产品，且累计通过兼容性产品数≥5款的企业单位。</p>
        </header>

        <div v-if="!partners.length" class="empty-wrap">
          <a-empty description="暂无潜在捐赠伙伴数据" />
        </div>

        <div v-else class="partner-grid">
          <article class="partner-card" v-for="(item, index) in partners" :key="item.companyName || index">
            <div class="card-top">
              <span class="rank-badge">{{ index + 1 }}</span>
              <div class="company-block">
                <div class="company-name">{{ item.companyName || '--' }}</div>
                <div class="company-subtext">通过兼容性产品数</div>
              </div>
            </div>
            <div class="card-bottom">
              <span class="metric-value">{{ formatNumber(item.productCount) }}<span class="metric-unit">款</span></span>
            </div>
          </article>
        </div>
      </section>
    </div>
  </a-spin>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';

const loading = ref(false);
const partners = ref([]);

const formatNumber = (value) => {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  return safeValue.toLocaleString('zh-CN');
};

const fetchPotentialDonorPartners = async () => {
  try {
    const res = await axios.get('/contributorController/getPotentialDonorPartner');
    partners.value = Array.isArray(res?.data?.data)
      ? [...res.data.data].sort((a, b) => Number(b.productCount) - Number(a.productCount))
      : [];
  } catch (error) {
    console.error('获取潜在捐赠伙伴失败：', error);
    notification.error({
      message: '获取潜在捐赠伙伴失败',
      description: error?.message || '请稍后再试'
    });
    partners.value = [];
  }
};

onMounted(async () => {
  loading.value = true;
  try {
    await fetchPotentialDonorPartners();
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.potential-donors-page {
  width: 100%;
  margin-bottom: 64px;
}

.partners-panel {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.panel-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf2f7;
  margin-bottom: 16px;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}

.panel-count {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 600;
}

.panel-note {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #64748b;
  text-align: left;
}

.empty-wrap {
  padding: 36px 0;
}

.partner-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.partner-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 84px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.partner-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}

.card-top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.rank-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #3b82f6;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.company-block {
  min-width: 0;
  text-align: left;
}

.company-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: #1f2937;
  word-break: break-word;
}

.company-subtext {
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.card-bottom {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
}

.metric-value {
  font-size: 14px;
  font-weight: 700;
  color: #2563eb;
  line-height: 1;
}

.metric-unit {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

@media (max-width: 1200px) {
  .partner-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .partner-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .partner-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}
</style>
